import {
  ArrowRightSmallGreyIcon as ArrowIcon,
  BankWebServiceIcon,
  PaymentNetworkIcon,
  ReferenceIcon,
  SepaIcon,
  SwiftIcon,
  TransferDetailsIcon
} from '@icons'
import applicationFeeImage from '@images/app-fee-no-funds-image.png'
import parse from 'html-react-parser'
import {
  MerchantEntityTypes,
  PaymentMethods,
  paths,
  useDepositInitiateMutation,
  useDepositPreviewMutation,
  useMerchantQry
} from 'mmfintech-backend-api'
import type { DepositRequest } from 'mmfintech-backend-api/types'
import { tr } from 'mmfintech-commons'
import { ErrorDisplay } from 'mmfintech-portal-commons'
import { useHistory } from 'react-router-dom'

import './ApplicationFeeDetailsModal.scss'

function ApplicationFeeDetailsModal() {
  const { merchant } = useMerchantQry()

  const history = useHistory()

  const [depositPreview, { error: previewError, isLoading: previewFetching }] = useDepositPreviewMutation()
  const [depositInitiate, { error: initiateError, isLoading: initiateFetching }] = useDepositInitiateMutation()

  async function initiatePayment(paymentMethod: string) {
    try {
      const data = {
        amount: merchant?.accountSetupFee?.amount,
        currency: merchant?.accountSetupFee?.currency,
        paymentMethod
      } as DepositRequest

      await depositPreview(data).unwrap()
      await depositInitiate(data).unwrap()

      history.push({
        pathname: paths.banking.depositPreview(),
        state: { paymentType: paymentMethod }
      })
    } catch {}
  }

  function handleSelectPaymentOption(paymentMethod: string) {
    if (
      merchant?.entityType === MerchantEntityTypes.COMPANY &&
      merchant?.accountSetupFee?.currency &&
      !previewFetching &&
      !initiateFetching
    ) {
      void initiatePayment(paymentMethod)
    }
  }

  return (
    <div className='application-fee-details-wrapper'>
      <img src={applicationFeeImage} alt='application-fee' />

      <div className='title'>
        {tr(
          'FRONTEND.APPLICATION_FEE.DETAILS.TITLE',
          'The Guardian Bank won’t take money from your bank automatically'
        )}
      </div>
      <div className='subtitle'>
        {tr('FRONTEND.APPLICATION_FEE.DETAILS.SUBTITLE', 'You’ll need to do this bit yourself to pay by bank transfer')}
      </div>

      <div className='payment-step'>
        <PaymentNetworkIcon />
        <div>
          {parse(
            tr(
              'FRONTEND.APPLICATION_FEE.DETAILS.STEP_1',
              'Choose the payment network of your preference - <b>SWIFT</b> or <b>SEPA</b>'
            )
          )}
        </div>
      </div>
      <div className='payment-step'>
        <TransferDetailsIcon />
        <div>
          {parse(tr('FRONTEND.APPLICATION_FEE.DETAILS.STEP_2', 'Our bank transfer details will appear on the screen '))}
        </div>
      </div>
      <div className='payment-step'>
        <BankWebServiceIcon />
        <div>
          {parse(
            tr(
              'FRONTEND.APPLICATION_FEE.DETAILS.STEP_3',
              'Log into your bank’s web service (or go to your bank) to transfer the funds'
            )
          )}
        </div>
      </div>
      <div className='payment-step'>
        <ReferenceIcon />
        <div>
          {parse(
            tr(
              'FRONTEND.APPLICATION_FEE.DETAILS.STEP_4',
              'Don’t forget to add the <b>reference</b>. It will help us process your deposit faster'
            )
          )}
        </div>
      </div>

      <ErrorDisplay error={[previewError, initiateError]} />

      <div className='buttons-container'>
        <div className='payment-method-button' onClick={() => handleSelectPaymentOption(PaymentMethods.SWIFT_BANKWIRE)}>
          <div className='left'>
            <SwiftIcon />
            <span>SWIFT</span>
          </div>
          <ArrowIcon />
        </div>
        <div className='payment-method-button' onClick={() => handleSelectPaymentOption(PaymentMethods.BANKWIRE)}>
          <div className='left'>
            <SepaIcon />
            <span>SEPA</span>
          </div>
          <ArrowIcon />
        </div>
      </div>
    </div>
  )
}

export default ApplicationFeeDetailsModal
